import POSConfigModule from "@/store/modules/pos-config";
import UserModule from "@/store/modules/user";
import ReceiptModule from "@/store/modules/receipt";
import { LogService } from "@/services/logging";
import dayjs from "dayjs";
import {
  IBookingReceipt,
  IReceiptPayment,
  IReceiptProduct,
  IVatToPrint,
  VatLineType,
} from "@/interfaces/receipts";
import { IReceiptPaymentDetails } from "@/types/db/accounting";
import {
  OrderJson,
  OrderLine,
  OrderLinePublicType,
  PaymentMethod,
  TransactionType,
} from "@smeetz/orders";
import { Discount, DiscountType, VATType } from "@smeetz/order-common";
import { formatCounter } from "@smeetz/counter-formatter";
import { getGroupInfo } from "@/utils/vue-helpers/group";
import { getReceiptPayments } from "@/utils/vue-helpers/accounting";
import { getNewestNonEmptyOrderSession } from "@/utils/vue-helpers/orders.helper";
import { formatDateTime } from "./dates";
import i18n from "@/lang";
// Simplified type alias to avoid TS2589 (excessively deep type instantiation)
// caused by vue-i18n v11's complex overloaded generics on t()
const t: (key: string, ...args: unknown[]) => string = (i18n.global as any).t;

/**
 * Sum the session's discount ledger per line id. `totalPriceWithDiscount` is
 * deprecated (always equals `totalPrice`), so the ledger is the only source of
 * a discount; values are signed and VOUCHER rows are a payment instrument, not
 * a price reduction.
 */
function sumDiscountsByLine(discounts: Discount[]): Map<string, number> {
  const byLine = new Map<string, number>();
  for (const discount of discounts) {
    if (discount.type === DiscountType.VOUCHER) {
      continue;
    }
    byLine.set(
      discount.lineId,
      (byLine.get(discount.lineId) ?? 0) + discount.value
    );
  }
  return byLine;
}

/**
 * Net discount for one line. A cancellation's ledger row is keyed to the
 * ORIGINAL sale line (`mainLineId`) — the same rule the compliance-common-v2
 * position mapper uses, so the receipt matches the signed document.
 */
function discountForLine(line: OrderLine, byLine: Map<string, number>): number {
  const own = byLine.get(line.id);
  if (own !== undefined) {
    return own;
  }
  if (line.quantity < 0 && line.mainLineId) {
    return byLine.get(line.mainLineId) ?? 0;
  }
  return 0;
}

/**
 * Group the order's recap lines into per-VAT-rate summary lines for the receipt.
 * Purchase lines (quantity >= 0) and cancellation lines (quantity < 0) are grouped
 * separately, mirroring the legacy Purchase/Cancellation VAT split. Amounts are in
 * major units (`OrderLine.totalPrice` net of the ledger discount / `vatValue`).
 *
 * @param lines the summary's consolidated order lines
 * @param discounts the session's per-line discount ledger (`orderSession.discounts`)
 * @returns one `IVatToPrint` per (rate, line type), with a 1-based `vatIndex`
 */
export function buildReceiptVats(
  lines: OrderLine[],
  discounts: Discount[] = []
): IVatToPrint[] {
  const lineDiscounts = sumDiscountsByLine(discounts);

  const groups = new Map<
    string,
    {
      rate: number;
      type: VatLineType;
      vatAmount: number;
      amountInclVat: number;
    }
  >();
  for (const line of lines) {
    const type =
      line.quantity < 0 ? VatLineType.Cancellation : VatLineType.Purchase;
    const key = `${line.vatRate}|${type}`;
    const group = groups.get(key) ?? {
      rate: line.vatRate,
      type,
      vatAmount: 0,
      amountInclVat: 0,
    };
    group.vatAmount += line.vatValue;
    const lineTotal = line.totalPrice - discountForLine(line, lineDiscounts);
    // Excluded VAT is charged on top of the line price, so the VAT amount is
    // added to the incl.-VAT total (for included VAT it is already part of it).
    group.amountInclVat +=
      line.vatType === VATType.EXCLUDED ? lineTotal + line.vatValue : lineTotal;
    groups.set(key, group);
  }

  return Array.from(groups.values()).map((group, index) => {
    const rate = `${(group.rate * 100).toFixed(2)}%`;
    return {
      name: rate,
      rate,
      vatAmount: Math.abs(group.vatAmount).toFixed(2),
      amountExclVat: Math.abs(group.amountInclVat - group.vatAmount).toFixed(2),
      amountInclVat: Math.abs(group.amountInclVat).toFixed(2),
      priceId: [],
      vatIndex: `(${index + 1})`,
      type: group.type,
    };
  });
}

/**
 * Map the order's recap lines to printable receipt products. Amounts are in major
 * units. Each product carries the `vatIndex` of its rate's VAT summary line.
 *
 * @param lines the summary's consolidated order lines
 * @param vats the VAT summary lines built by `buildReceiptVats`
 * @param discounts the session's per-line discount ledger (`orderSession.discounts`)
 * @returns receipt products in the shape the receipt template renders
 */
export function buildReceiptProducts(
  lines: OrderLine[],
  vats: IVatToPrint[],
  discounts: Discount[] = []
): IReceiptProduct[] {
  const lineDiscounts = sumDiscountsByLine(discounts);
  const products: IReceiptProduct[] = [];
  for (const line of lines) {
    const type =
      line.quantity < 0 ? VatLineType.Cancellation : VatLineType.Purchase;
    const rate = `${(line.vatRate * 100).toFixed(2)}%`;
    const vatIndex =
      vats.find((vat) => vat.rate === rate && vat.type === type)?.vatIndex ||
      "";

    const discount = discountForLine(line, lineDiscounts);
    const discountValue = Math.abs(discount);

    products.push({
      name: line.categoryName,
      priceName:
        line.itemType === OrderLinePublicType.RETAIL &&
        line.priceName === line.categoryName
          ? ""
          : line.priceName,
      startDateTime: line.startDateTime
        ? formatDateTime(new Date(line.startDateTime).toISOString())
        : "",
      quantity: Math.abs(line.quantity),
      priceValue: (line.totalPrice - discount).toFixed(2),
      unitPrice: (line.overridePrice ?? line.unitPrice).toFixed(2),
      discountValue: discountValue === 0 ? "" : discountValue.toFixed(2),
      discountPercentage:
        discountValue === 0 || line.totalPrice === 0
          ? ""
          : ((discountValue * 100) / Math.abs(line.totalPrice)).toFixed(0),
      vatIndex,
      hasCancelledTickets: line.quantity < 0,
    });
  }

  return products;
}

/**
 * Build the base `IBookingReceipt` for the selected order: products, per-rate VAT
 * summary, payments, totals, seller and payment metadata. Country compliance and
 * the receipt counter are applied separately (`applyCountryCompliance` /
 * `applyReceiptCounter`).
 *
 * @param order the selected order
 * @param options `isBill` prints a pre-payment bill: no payments, no signature
 * @returns the receipt data in the shape the receipt template renders
 */
export async function buildBaseReceiptInfo(
  order: OrderJson,
  options: { isBill?: boolean } = {}
): Promise<IBookingReceipt> {
  const groupInfo = await getGroupInfo();
  // A reopened order (Orders page → edit/reprint) starts a fresh EMPTY edit
  // session; the receipt documents the sale, so fall back to the newest
  // previous session that still carries lines — the completed one — instead
  // of printing an empty ticket.
  const orderSession =
    order.orderSession.orderLines.length > 0
      ? order.orderSession
      : [...order.previousSessions]
          .sort(
            (a, b) =>
              new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
          )
          .find((session) => session.orderLines.length > 0) ??
        order.orderSession;

  const vats = buildReceiptVats(
    orderSession.orderLines,
    orderSession.discounts ?? []
  );
  const products = buildReceiptProducts(
    orderSession.orderLines,
    vats,
    orderSession.discounts ?? []
  );

  // Receipt date/time: the latest payment, falling back to the order creation.
  let latestPayment = null;
  for (const payment of orderSession.payments) {
    if (payment.type !== TransactionType.PAYMENT) {
      continue;
    }
    if (
      !latestPayment ||
      dayjs(payment.createdAt).isAfter(dayjs(latestPayment.createdAt))
    ) {
      latestPayment = payment;
    }
  }
  const latestDateTime = dayjs(latestPayment?.createdAt || order.createdAt);
  const day = formatDateTime(latestDateTime.toISOString(), true);
  const hour = latestDateTime.format("HH:mm:ss");

  const payments: IReceiptPayment[] = options.isBill
    ? []
    : getReceiptPayments(orderSession);

  // Adyen payment details: printed only when the session has exactly one
  // Adyen payment (mirrors the legacy receipt), with a masked card number.
  let paymentDetails: IReceiptPaymentDetails | null = null;
  const adyenPayments = orderSession.payments.filter(
    (payment) => payment.paymentMethod === PaymentMethod.POS_ADYEN
  );
  if (adyenPayments.length === 1 && adyenPayments[0].adyenPaymentInfo) {
    paymentDetails = adyenPayments[0].adyenPaymentInfo
      .additionalData as unknown as IReceiptPaymentDetails;
    if (paymentDetails?.card) {
      paymentDetails.card = "*****" + paymentDetails.card;
    }
  }

  // Excluded VAT is not part of `sales`; it is charged on top of the line
  // prices. Signed by line type to match `taxTotalInt`'s convention.
  const excludedVatTotal = orderSession.orderLines.reduce(
    (sum, line) =>
      line.vatType === VATType.EXCLUDED
        ? sum + Math.abs(line.vatValue) * (line.quantity < 0 ? -1 : 1)
        : sum,
    0
  );
  // Excluded VAT is added on top of the sales total.
  const total = (
    orderSession.sales -
    orderSession.discount +
    excludedVatTotal
  ).toFixed(2);
  const taxTotalInt = vats.reduce(
    (sum, vat) =>
      sum +
      Number(vat.vatAmount) * (vat.type === VatLineType.Cancellation ? -1 : 1),
    0
  );
  const taxTotal = taxTotalInt.toFixed(2);
  // Only included VAT is baked into `sales`, so only that portion is removed
  // to reach the net total; excluded VAT is left out of the subtraction.
  const netTotal = (
    orderSession.sales -
    orderSession.discount -
    (taxTotalInt - excludedVatTotal)
  ).toFixed(2);

  const { firstName, lastName } = UserModule.user;

  return {
    isBill: options.isBill || false,
    products,
    groupInfo,
    bookingRef: order.reference,
    orderSessionReference: orderSession.reference,
    discountTotal:
      orderSession.discount > 0 ? orderSession.discount.toFixed(2) : "",
    total,
    netTotal,
    vat: "",
    vats,
    date: day,
    hour,
    currency: orderSession.currency || "CHF",
    payments,
    printVatLineLabels: orderSession.orderLines.some(
      (line) => line.quantity < 0
    ),
    sellerName: `${(firstName && firstName[0].toUpperCase()) || ""} ${
      (lastName && lastName[0].toUpperCase()) || ""
    }`,
    paymentDetails: options.isBill ? null : paymentDetails,
    taxTotal,
  };
}

/**
 * Stamp the receipt counter onto the receipt. Skipped in countries where the
 * fiscal third party provides the receipt reference. A reprint of an
 * already-printed counter increments it and marks the receipt as a copy.
 *
 * @param receiptInfo the receipt being prepared
 * @param order the order being printed, used to recover the original
 * counter number on copy receipts
 */
export async function applyReceiptCounter(
  receiptInfo: IBookingReceipt,
  order: OrderJson
): Promise<void> {
  const isAutoTestsUser =
    POSConfigModule.groupInfo?.id === 16632 &&
    import.meta.env.VITE_ENV === "test";

  const countryCode = POSConfigModule.getGroupCountryCode;
  const hasThirdPartyReference =
    countryCode === "FR" ||
    countryCode === "IT" ||
    countryCode === "AT" ||
    countryCode === "DE" ||
    countryCode === "BE" ||
    countryCode === "ES";

  if (!isAutoTestsUser && hasThirdPartyReference) {
    return;
  }

  // const originalReceiptCounterValue =
  //   getNewestNoneEmptyOrderSession(booking)?.receiptCounterValue || "";

  // A receipt that was already printed in this order session is a reprint.
  // Mark it as a copy so the COPY label is shown, but do NOT increment the
  // counter — a reprint must not consume a new receipt number (FIS-496).
  if (
    ReceiptModule.receiptCounter &&
    ReceiptModule.receiptCounter.isReceiptPrinted
  ) {
    ReceiptModule.setReceiptAsCopy();
    LogService.info(
      `Receipt counter ${ReceiptModule.receiptCounter.formattedValue} has already been printed; reprinting as a copy without incrementing the counter`,
      { params: ReceiptModule.receiptCounter }
    );
  }
  receiptInfo.orderSessionReference =
    ReceiptModule.receiptCounter?.formattedValue || "";

  if (ReceiptModule.isReceiptCopy) {
    receiptInfo.isReceiptCopy = true;
    // FIS-496: a copy keeps the ORIGINAL receipt number. A same-session
    // reprint already carries it (the counter is not incremented), but for an
    // order pulled again later — after the device counter moved on — the
    // number is rebuilt from the counter stamp (`seqNumber`) of the order's
    // newest non-empty session. Without a stamp the copy falls back to the
    // current counter value set above.
    const stampedSession = getNewestNonEmptyOrderSession(order);
    if (stampedSession?.seqNumber && ReceiptModule.receiptCounter) {
      receiptInfo.orderSessionReference = formatCounter({
        reference: ReceiptModule.receiptCounter.reference,
        value: stampedSession.seqNumber,
      });
    }
    receiptInfo.copyReceiptLabel = t("becert.copy");
  }
}
